/**
 * Daueraufnahme ohne native Hülle. (17.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Es gibt ja meinen Alltag, das geht ja nur mit der nativen App.
 * Könnte man die PWA aufpimpen, dass Recording auch geht … als Alternative,
 * bevor er die native App aktiviert?"* — und auf die Rückfrage: *„Albert
 * sitzt die meiste Zeit am Laptop und da könnte er es laufen lassen und
 * nebenbei arbeiten. Wenn er auf dem Handy in der PWA genutzt wird, dann
 * muss die Info einfach dabei stehen."*
 *
 * ## Was dieses Modul NICHT kann — und warum das keine Nachlässigkeit ist
 *
 * ⚠️⚠️ **Auf dem iPhone endet die Aufnahme, wenn der Bildschirm sperrt.**
 *
 *    Das ist keine fehlende Sorgfalt, sondern eine Regel von iOS: Eine
 *    Webseite bekommt im Hintergrund kein Mikrofon. Eine NATIVE App darf
 *    eine laufende Audio-Sitzung weiterführen (deshalb gibt es „Mein
 *    Alltag" dort), eine Webseite nicht. Wer hier einen Trick einbaut, baut
 *    einen, der beim nächsten iOS still aufhört zu wirken — und dann wieder
 *    jemand eine Stunde ins Leere spricht.
 *
 *    Deshalb: Dieses Modul VERSPRICHT nichts, was es nicht halten kann. Es
 *    sagt vorher, was gilt, und es sagt HÖRBAR Bescheid, wenn die Seite in
 *    den Hintergrund geht. Ein Ton erreicht jemanden, der nicht hinsieht;
 *    eine Einblendung nicht — das hat Albert am 16.09. eine halbe Stunde
 *    gekostet.
 *
 * ## Was es kann
 *
 * Am Rechner (und in jedem Browser, dessen Tab wach bleibt) läuft es
 * durch — Häppchen für Häppchen, dieselbe Schnittstelle wie die native
 * Aufnahme (`/api/recorder/chunk`, `/api/recorder/finish`). Das Ergebnis
 * landet damit im selben „Mein Alltag", mit derselben Verarbeitung,
 * demselben Transkript und derselben Segmentierung.
 *
 * ⚠️ Bewusst KEINE zweite Ablage und kein zweiter Weg zum Server. Zwei
 *    Pfade, die dasselbe tun, laufen auseinander — das ist in diesem Baum
 *    das häufigste Fehlermuster.
 */

/** Wie lang ein Häppchen ist. Gleiche Länge wie nativ — eine Minute. */
export const HAEPPCHEN_MS = 60_000;

type BrowserRecorderZustand = "idle" | "recording" | "finishing";

interface BrowserRecorderMeldung {
  /** Kurzer Satz für die Oberfläche. */
  text: string;
  art: "info" | "warnung";
}

export interface BrowserRecorderUmgebung {
  /** Mikrofon holen. Einspeisbar, damit Tests ohne Gerät laufen. */
  holeStrom: () => Promise<MediaStream>;
  /** Ein Häppchen hochladen. */
  sendeHaeppchen: (sitzung: string, seq: number, blob: Blob) => Promise<void>;
  /** Die Sitzung abschließen. */
  schliesseAb: (sitzung: string, beginn: string, grund: string) => Promise<void>;
  /** Hörbares Signal — erreicht auch, wer nicht hinsieht. */
  tonGeben: () => void;
  melde: (m: BrowserRecorderMeldung) => void;
  /** Für Tests: Zeitgeber und Zufall einspeisbar halten. */
  jetzt?: () => number;
  neueSitzung?: () => string;
}

/**
 * Läuft die Aufnahme hier zuverlässig durch, oder nur solange der Bildschirm
 * an ist?
 *
 * ⚠️ Die Unterscheidung ist NICHT „Handy gegen Rechner", sondern „darf die
 *    Seite im Hintergrund weiterarbeiten". Auf iOS gilt das für JEDEN
 *    Browser, auch für Chrome — dort steckt dieselbe WebKit-Engine darunter.
 */
export function laeuftImHintergrundWeiter(
  ua: string = typeof navigator === "undefined" ? "" : navigator.userAgent,
  maxTouch: number = typeof navigator === "undefined"
    ? 0
    : (navigator.maxTouchPoints ?? 0),
): boolean {
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPad ab iPadOS 13 meldet sich als Macintosh — nur die Touchpunkte
    // verraten es noch.
    (/Macintosh/.test(ua) && maxTouch > 1);
  if (iOS) return false;
  // Android-Browser drosseln im Hintergrund stark und beenden die Aufnahme
  // oft nach Minuten. Ehrlich ist hier ein Nein.
  if (/Android/.test(ua)) return false;
  return true;
}

/** Der Satz, der VOR dem Start dasteht — nicht danach. */
export function hinweisVorStart(zuverlaessig: boolean): BrowserRecorderMeldung {
  return zuverlaessig
    ? {
        text: "Aufnahme läuft, solange dieser Tab offen ist. Du kannst nebenbei weiterarbeiten.",
        art: "info",
      }
    : {
        text:
          "Auf dem Telefon nimmt der Browser nur auf, solange der Bildschirm an " +
          "und diese Seite vorn ist. Sperrst du oder wechselst die App, endet " +
          "die Aufnahme — du hörst dann einen Ton. Für Aufnahmen in der Tasche " +
          "brauchst du die App.",
        art: "warnung",
      };
}

export class BrowserRecorder {
  private rec: MediaRecorder | null = null;
  private strom: MediaStream | null = null;
  private seq = 0;
  private sitzung = "";
  private beginn = "";
  private zustand: BrowserRecorderZustand = "idle";
  /** Läuft ein Upload, damit sich zwei Häppchen nicht überholen. */
  private kette: Promise<unknown> = Promise.resolve();

  constructor(private u: BrowserRecorderUmgebung) {}

  getZustand(): BrowserRecorderZustand {
    return this.zustand;
  }

  async start(): Promise<void> {
    if (this.zustand !== "idle") return;
    const jetzt = this.u.jetzt ?? Date.now;
    this.sitzung =
      this.u.neueSitzung?.() ??
      `web-${jetzt().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    this.beginn = new Date(jetzt()).toISOString();
    this.seq = 0;
    this.strom = await this.u.holeStrom();
    const rec = new MediaRecorder(this.strom, { audioBitsPerSecond: 64_000 });
    this.rec = rec;
    rec.ondataavailable = (ev: BlobEvent): void => {
      if (!ev.data || ev.data.size === 0) return;
      /*
       * ⚠️ SOFORT hochladen, nicht sammeln. Die Sprechtaste sammelt alles
       *    im Speicher und schickt es erst am Ende — stirbt der Tab, ist
       *    alles weg. Bei einer Aufnahme über Stunden wäre das der
       *    sicherste Weg, viel zu verlieren.
       */
      const nr = this.seq++;
      const blob = ev.data;
      this.kette = this.kette
        .then(() => this.u.sendeHaeppchen(this.sitzung, nr, blob))
        .catch(() => {
          // Ein misslungenes Häppchen darf die Aufnahme nicht beenden —
          // die nächsten kommen weiter an.
          this.u.melde({
            text: "Ein Stück der Aufnahme kam nicht durch — es läuft weiter.",
            art: "warnung",
          });
        });
    };
    rec.start(HAEPPCHEN_MS);
    this.zustand = "recording";
    this.u.melde(hinweisVorStart(laeuftImHintergrundWeiter()));
  }

  /**
   * Die Seite geht in den Hintergrund.
   *
   * ⚠️ Auf iOS ist die Aufnahme damit vorbei, ob wir wollen oder nicht. Wir
   *    schließen sie deshalb SAUBER ab, statt sie halb offen sterben zu
   *    lassen — und geben einen Ton, weil niemand hinsieht.
   */
  async beiHintergrund(): Promise<void> {
    if (this.zustand !== "recording") return;
    if (laeuftImHintergrundWeiter()) return;
    this.u.tonGeben();
    await this.stop("seite-verlassen");
  }

  async stop(grund = "knopf"): Promise<void> {
    if (this.zustand !== "recording") return;
    this.zustand = "finishing";
    const rec = this.rec;
    if (rec && rec.state !== "inactive") {
      await new Promise<void>((fertig) => {
        rec.onstop = (): void => fertig();
        rec.stop();
      });
    }
    this.strom?.getTracks().forEach((t) => t.stop());
    this.strom = null;
    this.rec = null;
    // Erst wenn alle Häppchen durch sind, darf abgeschlossen werden.
    await this.kette.catch(() => undefined);
    await this.u.schliesseAb(this.sitzung, this.beginn, grund).catch(() => {
      this.u.melde({
        text: "Der Abschluss kam nicht durch — die Stücke sind aber oben.",
        art: "warnung",
      });
    });
    this.zustand = "idle";
  }
}
